import React, { useRef } from 'react';
import moment from 'moment-timezone';
import styled from 'styled-components';
import type { MenuProps } from 'antd';
import {
  CloseOutlined,
  CalendarOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  CloseCircleFilled,
} from '@ant-design/icons';
import { List, Flex, Spin, Badge, Empty, Dropdown, Typography } from 'antd';

import { t } from '../../utils/i18n';
import { useGlobals } from '@shared/contexts/GlobalsContext';
import { useAppSelector } from '../../shared/hooks/use-app-selector';
import { useAppDispatch } from '../../shared/hooks/use-app-dispatch';
import { getMerchantTimeZone } from '../../selectors';
import {
  notificationsAppointmentClick,
  notificationsAppointmentItemClick,
  notificationsAppointmentCloseClick,
} from '../../actions';
import type { AppointmentNotification } from '../../actions';
import NotificationsEmptyStateIcon from '../../assets/images/svg/notifications_empty_state.svg';

const STATUS_GROUPS = {
  CANCELLED: [
    'rejected',
    'rejected_by_customer',
    'canceled',
    'cancelled',
    'expired',
  ],
  PENDING: [
    'dispatched',
    'alternative_proposed',
    'waiting',
    'new_request',
    'customer_alternative_proposed',
  ],
  CONFIRMED: ['accepted', 'competing', 'confirmed', 'rescheduled_by_customer'],
};

const StyledNotificationsContainer = styled(Flex)`
  position: relative;
  line-height: normal;

  .ant-v5-dropdown {
    .ant-v5-dropdown-menu {
      padding: 1.25rem;
    }
    .ant-v5-dropdown-menu-item-group-title {
      padding: 0px;
      padding-bottom: 1rem;
    }

    .ant-v5-dropdown-menu-item {
      padding: 0px;

      &:hover {
        background-color: transparent;
      }
    }
  }
`;

const StyledHeaderIcon = styled.span`
  font-size: 2rem;
  cursor: pointer;
  color: ${({ theme }) => theme.antd.colorTextSecondary};

  &:hover {
    color: ${({ theme }) => theme.antd.colorPrimary};
  }
`;

export const StyledDropdownContentWrapper = styled(Flex)`
  width: 300px;
  max-height: 280px;
  overflow-y: auto;
`;

export const StyledNotificationsDropdown = styled(Dropdown)``;

export const StyledListWrapper = styled(Flex)`
  width: 100%;
  padding: 10px;
`;

export const StyledListEmpty = styled(Empty)`
  .ant-v5-empty-image {
    margin-bottom: 1.5rem;
  }
`;

export const StyledListTitle = styled(Typography.Text)`
  margin: 0;
  font-size: 12px;
  text-transform: uppercase;
`;

export const StyledListItem = styled(List.Item)`
  border: none !important;
  padding: 1rem !important;
  background-color: #fafafa;
  border-radius: 4px;
  cursor: pointer;

  &:not(:last-child) {
    margin-bottom: 10px;
  }

  &:hover {
    background-color: #f2f2f3;
  }
`;

const StyledCloseIcon = styled.span`
  outline: none;
  cursor: pointer;
  font-size: 1.4rem;
  color: rgba(15, 23, 42, 0.45);

  &:hover {
    color: #222222;
  }
`;

function NotificationStatusIcon({ status }: { status: string }) {
  if (STATUS_GROUPS.CANCELLED.includes(status)) {
    return <CloseCircleFilled style={{ fontSize: 18, color: '#FF2E52' }} />;
  }
  if (STATUS_GROUPS.PENDING.includes(status)) {
    return (
      <ExclamationCircleFilled style={{ fontSize: 18, color: '#69B1FF' }} />
    );
  }

  return <CheckCircleFilled style={{ fontSize: 18, color: '#00D0BE' }} />;
}

type NotificationsListProps = {
  isLoading: boolean;
  data: AppointmentNotification[];
  merchantTimeZone: string | null;
  onAppointmentNotificationClicked: (data: {
    groupId: string;
    notificationId: string;
    appointmentId: string;
    appointmentType: string;
  }) => void;
  onAppointmentNotificationCloseClicked: (data: {
    notificationId: string;
  }) => void;
};

// The API encodes appointment times with an offset that isn't necessarily the
// merchant's own — resolve the instant to the merchant's business timezone so
// the displayed wall-clock time doesn't depend on the offset the upstream
// service happened to emit or on the viewer's browser zone. If the merchant
// timezone is unavailable, fall back to the offset carried by the payload
// (rather than silently rendering UTC).
function formatInMerchantTimeZone(
  date: string,
  format: string,
  merchantTimeZone: string | null
): string {
  if (merchantTimeZone && moment.tz.zone(merchantTimeZone)) {
    return moment.tz(date, merchantTimeZone).format(format);
  }
  return moment.parseZone(date).format(format);
}

export function NotificationsList({
  isLoading,
  data,
  merchantTimeZone,
  onAppointmentNotificationClicked,
  onAppointmentNotificationCloseClicked,
}: Readonly<NotificationsListProps>) {
  const getRelativeTime = (timestamp: string): string => {
    const now = moment();
    const time = moment(timestamp);
    const duration = moment.duration(now.diff(time));

    const seconds = Math.floor(duration.asSeconds());
    const minutes = Math.floor(duration.asMinutes());
    const hours = Math.floor(duration.asHours());
    const days = Math.floor(duration.asDays());

    if (seconds < 60) {
      return t('notifications.appointment.relative-time.seconds-ago', {
        count: seconds,
      });
    }
    if (minutes < 60) {
      return t('notifications.appointment.relative-time.minutes-ago', {
        count: minutes,
      });
    }
    if (hours < 24) {
      return t('notifications.appointment.relative-time.hours-ago', {
        count: hours,
      });
    }
    return t('notifications.appointment.relative-time.days-ago', {
      count: days,
    });
  };

  if (isLoading) {
    return (
      <StyledListWrapper align="center" justify="center" vertical>
        <Spin />
      </StyledListWrapper>
    );
  }
  if (data.length === 0) {
    return (
      <StyledListWrapper align="center" justify="center" vertical>
        <StyledListEmpty
          image={<NotificationsEmptyStateIcon />}
          description={
            <Typography.Text type="secondary">
              {t('notifications.appointment.empty')}
            </Typography.Text>
          }
        />
      </StyledListWrapper>
    );
  }

  return (
    <StyledListWrapper vertical style={{ padding: 0 }}>
      <List
        itemLayout="horizontal"
        dataSource={data}
        renderItem={(item: AppointmentNotification) => (
          <StyledListItem
            key={item.id}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              onAppointmentNotificationClicked({
                groupId: item.groupId,
                notificationId: item.id,
                appointmentId: item.appointmentId,
                appointmentType: item.appointmentType,
              });
            }}
          >
            <Flex align="start" gap={12} style={{ width: '100%' }}>
              <Flex style={{ width: 20 }}>
                <NotificationStatusIcon status={item.status} />
              </Flex>
              <Flex vertical gap={4} style={{ flex: 1 }}>
                <Typography.Text
                  type="secondary"
                  style={{ fontSize: 11, textTransform: 'uppercase' }}
                >
                  {t(
                    `notifications.appointment.states.${item.status.replace(/_/g, '-')}.headline`
                  )}
                </Typography.Text>
                {item.previousDate && (
                  <Typography.Text
                    delete
                    type="secondary"
                    style={{ fontSize: 11 }}
                  >
                    {formatInMerchantTimeZone(
                      item.previousDate,
                      'ddd, DD.MM.YYYY HH:mm',
                      merchantTimeZone
                    )}
                  </Typography.Text>
                )}
                <Typography.Text strong style={{ fontSize: 11 }}>
                  {formatInMerchantTimeZone(
                    item.requestedDate,
                    item.previousDate
                      ? 'ddd, DD.MM.YYYY HH:mm'
                      : 'ddd, DD.MM.YYYY',
                    merchantTimeZone
                  )}
                </Typography.Text>
                <Typography.Text style={{ fontSize: 11 }}>
                  {item.services}
                </Typography.Text>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {item.customerName}
                </Typography.Text>
              </Flex>
              <Flex justify="end" gap={5} style={{ width: 80 }}>
                <Typography.Text type="secondary" style={{ fontSize: 11 }}>
                  {getRelativeTime(item.timestamp)}
                </Typography.Text>
                <StyledCloseIcon
                  onClick={(e: React.MouseEvent) => {
                    e.stopPropagation();
                    onAppointmentNotificationCloseClicked({
                      notificationId: item.id,
                    });
                  }}
                >
                  <CloseOutlined />
                </StyledCloseIcon>
              </Flex>
            </Flex>
          </StyledListItem>
        )}
      />
    </StyledListWrapper>
  );
}

export function AppointmentNotifications() {
  const dispatch = useAppDispatch();
  const { trackMethod } = useGlobals();
  const containerRef = useRef<HTMLDivElement>(null);
  const notifications = useAppSelector((state) => state.notifications);
  const merchantTimeZone = useAppSelector(getMerchantTimeZone);
  const appointmentNotificationsCount = notifications.appointmentCount || 0;

  const isLoadingAppointmentNotifications = !notifications.ui.loaded;
  const appointmentNotificationsData: AppointmentNotification[] =
    notifications.appointments;

  const handleOnDropdownOpenClicked = (open: boolean) => {
    if (open) {
      trackMethod('top_menu_item_clicked', {
        platform: 'booking',
        location: 'top_menu_button',
        button_type: 'appointment_notifications',
      });
      dispatch(notificationsAppointmentClick());
    }
  };

  const handleOnAppointmentNotificationClicked = (data: {
    groupId: string;
    notificationId: string;
    appointmentId: string;
    appointmentType: string;
  }) => {
    dispatch(
      notificationsAppointmentItemClick({
        id: data.notificationId,
        appointmentId: data.appointmentId,
        appointmentType: data.appointmentType,
        groupId: data.groupId,
      })
    );
  };

  const handleOnAppointmentNotificationCloseClicked = (data: {
    notificationId: string;
  }) => {
    dispatch(notificationsAppointmentCloseClick(data.notificationId));
  };

  const notificationDropdownContent = (
    <StyledDropdownContentWrapper vertical>
      <NotificationsList
        isLoading={isLoadingAppointmentNotifications}
        data={appointmentNotificationsData}
        merchantTimeZone={merchantTimeZone}
        onAppointmentNotificationClicked={
          handleOnAppointmentNotificationClicked
        }
        onAppointmentNotificationCloseClicked={
          handleOnAppointmentNotificationCloseClicked
        }
      />
    </StyledDropdownContentWrapper>
  );

  const notificationItems: MenuProps['items'] = [
    {
      key: 'header',
      type: 'group',
      label: (
        <StyledListTitle>
          {t('notifications.menu-header.appointment')}
        </StyledListTitle>
      ),
    },
    {
      key: 'content',
      label: notificationDropdownContent,
    },
    // TODO: Pagination is not yet supported by the backend API.
    // Add Load More functionality when pagination becomes available.
    // {
    //   key: 'load_more',
    //   label: (
    //     <Button
    //       block
    //       size="small"
    //       color="default"
    //       variant="outlined"
    //       style={{ marginTop: 10, fontSize: 12 }}
    //       key="appointment_notifications_load_more_button"
    //       data-testid="appointment_notifications_load_more_button"
    //     >
    //       Load More
    //     </Button>
    //   ),
    // },
  ];

  const handleOnAppointmentNotificationsIconClick = () => {
    trackMethod('menu_item_clicked', {
      platform: 'booking',
      location: 'top_menu_icons',
      menu_item: 'appointment_notifications',
    });
  };

  return (
    <StyledNotificationsContainer
      ref={containerRef}
      onClick={handleOnAppointmentNotificationsIconClick}
    >
      <StyledNotificationsDropdown
        arrow
        trigger={['click']}
        placement="bottomRight"
        menu={{ items: notificationItems }}
        onOpenChange={handleOnDropdownOpenClicked}
        getPopupContainer={() => containerRef.current! || document.body}
      >
        <Badge
          size="small"
          count={appointmentNotificationsCount}
          color="#00d0be"
          style={{ fontSize: 8, padding: '0 4px' }}
        >
          <StyledHeaderIcon>
            <CalendarOutlined />
          </StyledHeaderIcon>
        </Badge>
      </StyledNotificationsDropdown>
    </StyledNotificationsContainer>
  );
}
